import type { T } from "../../i18n";

// Pieces shared by the items screens: exact decimal display, the Code 128 barcode drawing and sample EAN-13 codes.

/**
 * Shows a decimal from the API (exact text such as "12.5000") without floating point: trailing zeros beyond
 * `min` decimals are dropped, so a price reads "12.50" and a weighed price keeps "1.2345".
 */
export function dec(x: string, min = 2): string {
  const [i, f = ""] = x.split(".");
  const kept = f.replace(/0+$/, "");
  const frac = kept.length >= min ? kept : f.slice(0, min).padEnd(min, "0");
  return frac ? `${i}.${frac}` : i!;
}
/** Pieces and quantities: "12.0000" reads "12", "2.5000" reads "2.5". */
export const qty = (x: string) => dec(x, 0);
export const pct = (x: string) => `${dec(x, 0)}%`;

// Code 128 (set B) as SVG, from the prototype (erp-items.js barcodeSvg).
const C128 = "212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 114131 311141 411131 211412 211214 211232".split(" ");
/** The Code 128 (set B) module widths of a text, bars and spaces alternating, starting with a bar. */
export function code128Widths(text: string): string {
  const vals = [104, ...[...text].map((ch) => Math.max(0, Math.min(94, ch.charCodeAt(0) - 32)))];
  const check = vals.reduce((a, v, i) => a + v * (i || 1), 0) % 103;
  return [...vals, check].map((v) => C128[v]).join("") + "2331112";
}
export function Barcode({ text, h = 46 }: { text: string; h?: number }) {
  const widths = code128Widths(text);
  let x = 10;
  const bars: [number, number][] = [];
  [...widths].forEach((w, i) => { const n = +w; if (i % 2 === 0) bars.push([x, n]); x += n; });
  return <svg className="bc-svg" viewBox={`0 0 ${x + 10} ${h}`} preserveAspectRatio="none" role="img" aria-label={text} data-testid="barcode-svg">{bars.map(([bx, bw]) => <rect key={bx} x={bx} y={0} width={bw} height={h} />)}</svg>;
}

/** A 13-digit code with the Saudi GS1 prefix 628 and a valid EAN-13 check digit (sample numbers, prototype rule). */
export function sampleEan13(): string {
  const body = "628" + String(Math.floor(Math.random() * 1e9)).padStart(9, "0");
  const sum = [...body].reduce((a, d, i) => a + Number(d) * (i % 2 ? 3 : 1), 0);
  return body + ((10 - (sum % 10)) % 10);
}

/** Barcodes are 1 to 48 printable characters without spaces (Delta rule, docs/architecture.md). */
export const BARCODE_RE = /^[!-~]{1,48}$/;

export const pendingCell = (t: T, module: string) => <span className="muted" title={t("waitsFor", { m: module })}>{t("pendingShort")}</span>;
